/**
 * What the front door shows, as a decision rather than a screen.
 *
 * Kept apart from the component because it is the rule, and rules are worth
 * a test: the app's own sign-in screen exists only in the desktop app, and
 * sending a website visitor to it put the router in a loop until the page
 * gave up.
 */
export type Door =
  /** The app's sign-in screen, which only the desktop app has. */
  | 'sign-in'
  /** The web's own door: where the terminal is, and how to knock. */
  | 'door'
  /** Whether they are in is not known yet. */
  | 'unknown'
  /** Signed in, waiting to be let in. */
  | 'queue'
  /** In. */
  | 'terminal'

export interface DoorState {
  isLoggedIn: boolean
  /** The desktop app, or a browser standing in for it. */
  isApp: boolean
  /** Undefined until the server has answered. */
  granted: boolean | undefined
  /**
   * This page is open to anybody who finds it.
   *
   * Only the market pages are ever this: the feed and a token. They read
   * endpoints that take no cookie and show the same thing to everyone, so
   * there is nothing behind the door to protect on them. The caller decides
   * which pages those are and whether this build opens them at all — the
   * rule only has to know that an open page is open.
   */
  isPublicPage: boolean
}

export const chooseDoor = ({
  isLoggedIn,
  isApp,
  granted,
  isPublicPage,
}: DoorState): Door => {
  // Before anything else, including the signed-in checks: an open page is
  // open to a visitor who is signed in and still queueing just as much as to
  // one who has never been here. Asking somebody already in the queue to
  // look at their own position before they may look at a chart would be a
  // door for the sake of having one.
  if (isPublicPage) {
    return 'terminal'
  }

  if (!isLoggedIn) {
    return isApp ? 'sign-in' : 'door'
  }

  if (granted === undefined) {
    return 'unknown'
  }

  return granted ? 'terminal' : 'queue'
}
